import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, DollarSign, FileText, User as UserIcon, X, Clock, Check, AlertCircle, AlertTriangle, CheckCircle, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({ upcomingAppointments: 0, totalTreatments: 0, outstandingBalance: 0, lastVisit: null });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [counterProposals, setCounterProposals] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [medicalHistoryDone, setMedicalHistoryDone] = useState(null);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [treatmentsRes, paymentsRes, appointmentsRes, requestsRes, medHistRes] = await Promise.all([
        axios.get(`${API}/patients/treatments`, { headers }),
        axios.get(`${API}/patients/payments`, { headers }),
        axios.get(`${API}/appointments`, { headers }),
        axios.get(`${API}/appointments/my-requests`, { headers }),
        axios.get(`${API}/patients/medical-history`, { headers })
      ]);
      const treatments = treatmentsRes.data;
      const payments = paymentsRes.data;
      const appointments = appointmentsRes.data;
      const requests = requestsRes.data;
      const totalCost = treatments.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);
      const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const pendingAmount = payments.filter(p => p.status !== 'paid').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const treatmentBalance = Math.max(totalCost - totalPaid, 0);
      const outstanding = Math.max(treatmentBalance, pendingAmount);
      const upcoming = appointments;
      setStats({ upcomingAppointments: upcoming.length, totalTreatments: treatments.length, outstandingBalance: outstanding, lastVisit: treatments.length > 0 ? treatments[0].date : null });
      setUpcomingAppointments(upcoming);
      setCounterProposals(requests.filter(r => r.status === 'counter_proposed'));
      const mh = medHistRes.data;
      setMedicalHistoryDone(mh && mh.exists !== false && mh.locked === true);
    } catch (error) { console.error('Failed to fetch dashboard data:', error); }
    finally { setLoading(false); }
  };

  const handleRequestAppointment = async (e) => {
    e.preventDefault();
    if (!appointmentDate || !appointmentTime) { toast.error(t.preferredDate + ' & ' + t.preferredTime); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments/request`, { requested_date: appointmentDate, requested_time: appointmentTime, notes: appointmentNotes || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.sendRequest + ' - OK!');
      setShowModal(false); setAppointmentDate(''); setAppointmentTime(''); setAppointmentNotes('');
    } catch (error) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleRespondToProposal = async (requestId, action) => {
    try {
      const res = await axios.put(`${API}/appointments/respond/${requestId}`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchDashboardData();
    } catch (error) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          {t.welcomeUser} {user?.name}!
        </h1>
        <p className="text-[#6B7280]">{t.dashboardSubtitle}</p>
      </div>

      {medicalHistoryDone !== null && !medicalHistoryDone && (
        <Link to="/portal/medical-history" className="block mb-6" data-testid="medical-history-alert">
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-center gap-3 hover:bg-red-100 transition-colors">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0"><AlertTriangle className="w-6 h-6 text-red-600" /></div>
            <div className="flex-1">
              <div className="font-semibold text-red-900">{t.completeMedicalHistory}</div>
              <div className="text-sm text-red-700">{t.completeMedicalHistoryDesc}</div>
            </div>
            <div className="text-red-600 font-semibold text-sm shrink-0">{t.fillNow} &rarr;</div>
          </div>
        </Link>
      )}
      {medicalHistoryDone === true && (
        <Link to="/portal/medical-history" className="block mb-6" data-testid="medical-history-complete">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3 hover:bg-emerald-100 transition-colors">
            <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-emerald-900">{t.medicalHistoryComplete}</div>
              <div className="text-sm text-emerald-700">{t.medicalHistoryOnFile}</div>
            </div>
          </div>
        </Link>
      )}

      {counterProposals.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="counter-proposals">
          {counterProposals.map(cp => (
            <div key={cp.id} className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid={`counter-proposal-${cp.id}`}>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 mb-1">{t.newAppointmentProposal}</div>
                  <p className="text-sm text-amber-800 mb-3">
                    {t.clinicSuggestedTime}
                    <strong className="ml-1">{new Date(cp.proposed_date).toLocaleDateString()} {t.at} {cp.proposed_time}</strong>
                    {cp.admin_note && <span className="block mt-1 italic">{t.note}: {cp.admin_note}</span>}
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => handleRespondToProposal(cp.id, 'accept')} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors" data-testid={`accept-proposal-${cp.id}`}><Check className="w-4 h-4" /> {t.accept}</button>
                    <button onClick={() => handleRespondToProposal(cp.id, 'reject')} className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium transition-colors" data-testid={`reject-proposal-${cp.id}`}><X className="w-4 h-4" /> {t.decline}</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {upcomingAppointments.length > 0 && (
        <div className="mb-8 bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="upcoming-appointments-section">
          <h2 className="text-xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.upcomingAppointments}</h2>
          <div className="space-y-3">
            {upcomingAppointments.map(apt => (
              <div key={apt.id} className="flex items-center gap-4 p-4 bg-[#0F4C5C]/5 rounded-lg border border-[#0F4C5C]/10" data-testid={`upcoming-apt-${apt.id}`}>
                <div className="w-12 h-12 bg-[#0F4C5C]/10 rounded-lg flex items-center justify-center shrink-0"><Calendar className="w-6 h-6 text-[#0F4C5C]" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-[#1F2937]">{new Date(apt.date).toLocaleDateString()}</div>
                  <div className="text-sm text-[#6B7280] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {apt.time}</div>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">{t.scheduled}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-appointments">
          <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-[#0F4C5C]/10 rounded-lg flex items-center justify-center"><Calendar className="w-6 h-6 text-[#0F4C5C]" /></div></div>
          <div className="text-2xl font-bold text-[#1F2937]">{stats.upcomingAppointments}</div>
          <div className="text-sm text-[#6B7280]">{t.statUpcomingAppointments}</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-treatments">
          <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-[#98D8CA]/20 rounded-lg flex items-center justify-center"><FileText className="w-6 h-6 text-[#0F4C5C]" /></div></div>
          <div className="text-2xl font-bold text-[#1F2937]">{stats.totalTreatments}</div>
          <div className="text-sm text-[#6B7280]">{t.statTotalTreatments}</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-balance">
          <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-[#E07A5F]/10 rounded-lg flex items-center justify-center"><DollarSign className="w-6 h-6 text-[#E07A5F]" /></div></div>
          <div className="text-2xl font-bold text-[#1F2937]">${stats.outstandingBalance.toFixed(2)}</div>
          <div className="text-sm text-[#6B7280]">{t.statOutstandingBalance}</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-last-visit">
          <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-[#0F4C5C]/10 rounded-lg flex items-center justify-center"><UserIcon className="w-6 h-6 text-[#0F4C5C]" /></div></div>
          <div className="text-2xl font-bold text-[#1F2937]">{stats.lastVisit ? new Date(stats.lastVisit).toLocaleDateString() : 'N/A'}</div>
          <div className="text-sm text-[#6B7280]">{t.statLastVisit}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm">
          <h2 className="text-xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.quickActions}</h2>
          <div className="space-y-3">
            <Link to="/portal/history" className="block p-4 bg-[#F9FAFB] rounded-lg hover:bg-[#98D8CA]/10 transition-colors" data-testid="view-history-link">
              <div className="font-semibold text-[#1F2937]">{t.viewTreatmentHistory}</div>
              <div className="text-sm text-[#6B7280]">{t.viewTreatmentHistoryDesc}</div>
            </Link>
            <Link to="/portal/payments" className="block p-4 bg-[#F9FAFB] rounded-lg hover:bg-[#98D8CA]/10 transition-colors" data-testid="view-payments-link">
              <div className="font-semibold text-[#1F2937]">{t.viewPaymentRecords}</div>
              <div className="text-sm text-[#6B7280]">{t.viewPaymentRecordsDesc}</div>
            </Link>
            <button onClick={() => setShowModal(true)} className="block w-full text-left p-4 bg-[#F9FAFB] rounded-lg hover:bg-[#98D8CA]/10 transition-colors" data-testid="book-appointment-button">
              <div className="font-semibold text-[#1F2937]">{t.bookNewAppointment}</div>
              <div className="text-sm text-[#6B7280]">{t.bookNewAppointmentDesc}</div>
            </button>
          </div>
        </div>
        <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0F4C5C]/80 p-8 rounded-lg text-white shadow-sm">
          <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.needHelp}</h2>
          <p className="text-white/90 mb-6">{t.needHelpDesc}</p>
          <div className="space-y-3">
            <div className="flex items-center"><Calendar className="w-5 h-5 mr-3" /><div><div className="font-semibold">{t.callUs}</div><div className="text-sm text-white/80">70059031</div></div></div>
            <div className="flex items-center"><Mail className="w-5 h-5 mr-3" /><div><div className="font-semibold">{t.emailUs || 'Email Us'}</div><a href="mailto:info@younesdental.com" className="text-sm text-white/80 hover:text-white">info@younesdental.com</a></div></div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="appointment-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="appointment-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-[#1F2937]" style={{ fontFamily: 'Playfair Display, serif' }}>{t.requestAppointment}</h2>
              <button onClick={() => setShowModal(false)} className="text-[#6B7280] hover:text-[#1F2937]" data-testid="close-modal-button"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleRequestAppointment} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2"><Calendar className="w-4 h-4 inline mr-1" /> {t.preferredDate}</label>
                <input type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)} min={new Date().toISOString().split('T')[0]} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="appointment-date-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2"><Clock className="w-4 h-4 inline mr-1" /> {t.preferredTime}</label>
                <input type="time" value={appointmentTime} onChange={(e) => setAppointmentTime(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="appointment-time-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.notesOptional}</label>
                <textarea value={appointmentNotes} onChange={(e) => setAppointmentNotes(e.target.value)} rows={3} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] resize-none" placeholder="..." data-testid="appointment-notes-input" />
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50" data-testid="submit-appointment-request">
                {submitting ? t.sending : t.sendRequest}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
